import React from 'react';
import styled from 'styled-components';
import { Settings, Check, X, Edit, Save, Trash2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.lg};
`;

const Header = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const TableContainer = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: ${props => props.theme.colors.background};
`;

const Th = styled.th`
  padding: ${props => props.theme.spacing.md};
  text-align: left;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Td = styled.td`
  padding: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
`;

const Badge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 0.875rem;
  font-weight: 500;
  background: ${props => props.bg || props.theme.colors.background};
  color: ${props => props.color || props.theme.colors.text};
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const LoadingState = styled.div`
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
`;

const EmptyState = styled.div`
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
`;

const Button = styled.button`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.bg || props.theme.colors.primary};
  color: white;
  cursor: pointer;
  font-size: 0.875rem;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &:hover {
    opacity: 0.9;
  }
`;

const Input = styled.input`
  padding: ${props => props.theme.spacing.xs};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 0.875rem;
  width: 100%;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: .4s;
    border-radius: 24px;
    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: .4s;
      border-radius: 50%;
    }
  }
  input:checked + .slider {
    background-color: ${props => props.theme.colors.primary};
  }
  input:checked + .slider:before {
    transform: translateX(20px);
  }
`;

function Providers() {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = React.useState(null);
  const [editData, setEditData] = React.useState({});

  const { data, isLoading, error } = useQuery(
    'providers',
    () => adminAPI.getProviders(),
    {
      refetchInterval: 60000, // Увеличено до 60 секунд для снижения нагрузки
      refetchOnWindowFocus: false, // Не обновлять при возврате фокуса
      refetchOnReconnect: false, // Не обновлять автоматически при восстановлении соединения
      retry: 1, // Уменьшено до 1 попытки
      retryDelay: 5000, // Фиксированная задержка 5 секунд
      staleTime: 30000, // Данные считаются свежими 30 секунд (увеличено)
      cacheTime: 300000, // Хранить в кэше 5 минут
      onError: (err) => {
        console.error('Providers error:', err);
      }
    }
  );

  const updateMutation = useMutation(
    ({ providerId, data: updateData }) => adminAPI.updateProvider(providerId, updateData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('providers');
        setEditingId(null);
        setEditData({});
        toast.success('Провайдер обновлен');
      },
      onError: (err) => {
        toast.error(`Ошибка: ${err.message || 'Не удалось обновить провайдер'}`);
      }
    }
  );

  const createMutation = useMutation(
    (data) => adminAPI.createProvider(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('providers');
        setEditingId(null);
        setEditData({});
        toast.success('Провайдер создан');
      },
      onError: (err) => {
        toast.error(`Ошибка: ${err.response?.data?.detail || err.message || 'Не удалось создать провайдер'}`);
      }
    }
  );

  const deleteMutation = useMutation(
    (providerId) => adminAPI.deleteProvider(providerId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('providers');
        toast.success('Провайдер удален');
      },
      onError: (err) => {
        toast.error(`Ошибка: ${err.response?.data?.detail || err.message || 'Не удалось удалить провайдер'}`);
      }
    }
  );

  const handleDelete = (providerId, providerName) => {
    if (window.confirm(`Вы уверены, что хотите удалить провайдер "${providerName}"?`)) {
      deleteMutation.mutate(providerId);
    }
  };

  const handleEdit = (provider) => {
    setEditingId(provider.id);
    setEditData({
      name: provider.name,
      type: provider.type,
      price_per_unit: provider.price_per_unit || provider.price_min || 0,
      price_min: provider.price_min || provider.price_per_unit || 0,
      price_max: provider.price_max || provider.price_per_unit || 0,
      enabled: provider.enabled,
      models: Array.isArray(provider.models) ? provider.models.join(', ') : '',
      capabilities: JSON.stringify(provider.capabilities || {}, null, 2),
      api_config: JSON.stringify(provider.api_config || {}, null, 2)
    });
  };

  const handleCreate = () => {
    setEditingId('new');
    setEditData({
      name: '',
      type: 'image',
      price_per_unit: 1.0,
      enabled: true,
      models: '',
      api_config: '{}'
    });
  };

  const handleSave = (providerId) => {
    if (providerId === 'new') {
      // Создание нового провайдера
      if (!editData.name || !editData.type) {
        toast.error('Укажите название и тип провайдера');
        return;
      }
      
      const createData = {
        name: editData.name,
        type: editData.type,
        price_per_unit: parseFloat(editData.price_per_unit) || 0,
        price_min: editData.price_min ? parseFloat(editData.price_min) : undefined,
        price_max: editData.price_max ? parseFloat(editData.price_max) : undefined,
        enabled: editData.enabled !== undefined ? editData.enabled : true,
      };
      
      // Парсить models если указаны
      if (editData.models) {
        try {
          createData.models = editData.models.split(',').map(m => m.trim()).filter(m => m);
        } catch (e) {
          toast.error('Неверный формат моделей');
          return;
        }
      }
      
      // Парсить capabilities если указаны
      if (editData.capabilities) {
        try {
          createData.capabilities = JSON.parse(editData.capabilities);
        } catch (e) {
          toast.error('Неверный JSON в capabilities');
          return;
        }
      }
      
      // Парсить api_config если указан
      if (editData.api_config) {
        try {
          createData.api_config = JSON.parse(editData.api_config);
        } catch (e) {
          toast.error('Неверный JSON в api_config');
          return;
        }
      }
      
      createMutation.mutate(createData);
    } else {
      // Обновление существующего провайдера
      const updateData = {};
      
      // Обновление названия
      if (editData.name !== undefined) {
        updateData.name = editData.name;
      }
      
      // Обновление типа (если нужно)
      if (editData.type !== undefined) {
        updateData.type = editData.type;
      }
      
      // Обновление цен
      if (editData.price_per_unit !== undefined) {
        updateData.price_per_unit = parseFloat(editData.price_per_unit) || 0;
      }
      if (editData.price_min !== undefined) {
        updateData.price_min = parseFloat(editData.price_min);
      }
      if (editData.price_max !== undefined) {
        updateData.price_max = parseFloat(editData.price_max);
      }
      
      // Обновление статуса
      if (editData.enabled !== undefined) {
        updateData.enabled = editData.enabled;
      }
      
      // Парсить models если указаны
      if (editData.models !== undefined) {
        try {
          updateData.models = editData.models ? editData.models.split(',').map(m => m.trim()).filter(m => m) : [];
        } catch (e) {
          toast.error('Неверный формат моделей');
          return;
        }
      }
      
      // Парсить capabilities если указаны
      if (editData.capabilities !== undefined) {
        try {
          updateData.capabilities = JSON.parse(editData.capabilities);
        } catch (e) {
          toast.error('Неверный JSON в capabilities');
          return;
        }
      }
      
      // Парсить api_config если указан
      if (editData.api_config !== undefined) {
        try {
          updateData.api_config = JSON.parse(editData.api_config);
        } catch (e) {
          toast.error('Неверный JSON в api_config');
          return;
        }
      }
      
      updateMutation.mutate({ providerId, data: updateData });
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ru-RU');
  };

  return (
    <Container>
      <Header>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title>
            <Settings size={32} />
            Провайдеры
          </Title>
          <Button 
            bg="#4f46e5" 
            onClick={handleCreate}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Settings size={16} />
            Создать провайдер
          </Button>
        </div>
      </Header>

      {isLoading ? (
        <LoadingState>Загрузка провайдеров...</LoadingState>
      ) : error ? (
        <EmptyState>Ошибка загрузки данных</EmptyState>
      ) : editingId === 'new' ? (
        <TableContainer>
          <div style={{ padding: '24px' }}>
            <h2 style={{ marginBottom: '24px', color: '#1f2937' }}>Создать новый провайдер</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', maxWidth: '900px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                  Название *
                </label>
                <Input
                  type="text"
                  placeholder="flux_kontext"
                  value={editData.name || ''}
                  onChange={(e) => setEditData({...editData, name: e.target.value})}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                  Тип *
                </label>
                <select
                  style={{
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    width: '100%'
                  }}
                  value={editData.type || 'image'}
                  onChange={(e) => setEditData({...editData, type: e.target.value})}
                >
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                  <option value="text">Text</option>
                  <option value="faceswap">FaceSwap</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                  Цена за единицу (кредиты) *
                </label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="1.5"
                  value={editData.price_per_unit || ''}
                  onChange={(e) => setEditData({...editData, price_per_unit: e.target.value})}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                  Модели (через запятую)
                </label>
                <Input
                  type="text"
                  placeholder="model1, model2, model3"
                  value={editData.models || ''}
                  onChange={(e) => setEditData({...editData, models: e.target.value})}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                  Цена мин (кредиты)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="1.0"
                  value={editData.price_min || ''}
                  onChange={(e) => setEditData({...editData, price_min: e.target.value})}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                  Цена макс (кредиты)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="2.0"
                  value={editData.price_max || ''}
                  onChange={(e) => setEditData({...editData, price_max: e.target.value})}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                  Capabilities (JSON)
                </label>
                <textarea
                  style={{
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    width: '100%',
                    minHeight: '100px',
                    fontFamily: 'monospace'
                  }}
                  placeholder='{"ratio": ["1:1", "16:9"], "variations": [1, 2, 3, 4]}'
                  value={editData.capabilities || '{}'}
                  onChange={(e) => setEditData({...editData, capabilities: e.target.value})}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                  API Config (JSON)
                </label>
                <textarea
                  style={{
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    width: '100%',
                    minHeight: '100px',
                    fontFamily: 'monospace'
                  }}
                  placeholder='{"api_key": "...", "endpoint": "..."}'
                  value={editData.api_config || '{}'}
                  onChange={(e) => setEditData({...editData, api_config: e.target.value})}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', gridColumn: '1 / -1' }}>
                <ToggleSwitch>
                  <input
                    type="checkbox"
                    checked={editData.enabled !== undefined ? editData.enabled : true}
                    onChange={(e) => setEditData({...editData, enabled: e.target.checked})}
                  />
                  <span className="slider" />
                </ToggleSwitch>
                <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Включен</span>
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <Button bg="#059669" onClick={() => handleSave('new')}>
                  <Save size={14} />
                  Создать провайдер
                </Button>
                <Button bg="#6b7280" onClick={handleCancel}>
                  Отмена
                </Button>
              </div>
            </div>
          </div>
        </TableContainer>
      ) : !data || !data.data || !data.data.providers || data.data.providers.length === 0 ? (
        <EmptyState>
          <div style={{ marginBottom: '16px' }}>Провайдеров не найдено</div>
          <Button bg="#4f46e5" onClick={handleCreate}>
            <Settings size={16} />
            Создать первый провайдер
          </Button>
        </EmptyState>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHeader>
                <tr>
                  <Th>ID</Th>
                  <Th>Название</Th>
                  <Th>Тип</Th>
                  <Th>Цена/ед (plan.md)</Th>
                  <Th>Модели</Th>
                  <Th>Статус</Th>
                  <Th>Действия</Th>
                </tr>
              </TableHeader>
              <tbody>
                {data.data.providers.map((provider) => (
                  editingId === provider.id ? (
                    // Форма редактирования - показываем как отдельную строку
                    <tr key={`edit-${provider.id}`} style={{ backgroundColor: '#f9fafb' }}>
                      <Td colSpan="7">
                        <div style={{ padding: '24px', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                          <h3 style={{ marginBottom: '20px', color: '#1f2937' }}>Редактирование провайдера: {provider.name}</h3>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', maxWidth: '900px' }}>
                            <div>
                              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                                Название *
                              </label>
                              <Input
                                type="text"
                                value={editData.name || ''}
                                onChange={(e) => setEditData({...editData, name: e.target.value})}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                                Тип *
                              </label>
                              <select
                                style={{
                                  padding: '8px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '6px',
                                  fontSize: '0.875rem',
                                  width: '100%'
                                }}
                                value={editData.type || 'image'}
                                onChange={(e) => setEditData({...editData, type: e.target.value})}
                              >
                                <option value="image">Image</option>
                                <option value="video">Video</option>
                                <option value="text">Text</option>
                                <option value="faceswap">FaceSwap</option>
                              </select>
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                                Цена за единицу (кредиты) *
                              </label>
                              <Input
                                type="number"
                                step="0.01"
                                value={editData.price_per_unit || 0}
                                onChange={(e) => setEditData({...editData, price_per_unit: e.target.value})}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                                Цена мин (кредиты)
                              </label>
                              <Input
                                type="number"
                                step="0.01"
                                value={editData.price_min || ''}
                                onChange={(e) => setEditData({...editData, price_min: e.target.value})}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                                Цена макс (кредиты)
                              </label>
                              <Input
                                type="number"
                                step="0.01"
                                value={editData.price_max || ''}
                                onChange={(e) => setEditData({...editData, price_max: e.target.value})}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                                Модели (через запятую)
                              </label>
                              <Input
                                type="text"
                                placeholder="model1, model2"
                                value={editData.models || ''}
                                onChange={(e) => setEditData({...editData, models: e.target.value})}
                              />
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                                Capabilities (JSON)
                              </label>
                              <textarea
                                style={{
                                  padding: '8px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '6px',
                                  fontSize: '0.875rem',
                                  width: '100%',
                                  minHeight: '100px',
                                  fontFamily: 'monospace'
                                }}
                                placeholder='{"ratio": ["1:1", "16:9"], "variations": [1, 2, 3, 4]}'
                                value={editData.capabilities || '{}'}
                                onChange={(e) => setEditData({...editData, capabilities: e.target.value})}
                              />
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                                API Config (JSON)
                              </label>
                              <textarea
                                style={{
                                  padding: '8px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '6px',
                                  fontSize: '0.875rem',
                                  width: '100%',
                                  minHeight: '100px',
                                  fontFamily: 'monospace'
                                }}
                                placeholder='{"api_key": "...", "endpoint": "..."}'
                                value={editData.api_config || '{}'}
                                onChange={(e) => setEditData({...editData, api_config: e.target.value})}
                              />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', gridColumn: '1 / -1' }}>
                              <ToggleSwitch>
                                <input
                                  type="checkbox"
                                  checked={editData.enabled !== undefined ? editData.enabled : true}
                                  onChange={(e) => setEditData({...editData, enabled: e.target.checked})}
                                />
                                <span className="slider" />
                              </ToggleSwitch>
                              <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Включен</span>
                            </div>
                            <div style={{ display: 'flex', gap: '12px', gridColumn: '1 / -1', marginTop: '8px' }}>
                              <Button bg="#059669" onClick={() => handleSave(provider.id)}>
                                <Save size={14} />
                                Сохранить изменения
                              </Button>
                              <Button bg="#6b7280" onClick={handleCancel}>
                                Отмена
                              </Button>
                              <Button bg="#dc2626" onClick={() => handleDelete(provider.id, provider.name)}>
                                <Trash2 size={14} />
                                Удалить провайдер
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Td>
                    </tr>
                  ) : (
                    // Обычная строка таблицы
                    <tr key={provider.id}>
                      <Td>{provider.id}</Td>
                      <Td>{provider.name}</Td>
                      <Td>
                        <Badge bg="#e0e7ff" color="#4f46e5">{provider.type}</Badge>
                      </Td>
                      <Td>
                        <span>{provider.price_per_unit || provider.price_min || 0} кр.</span>
                      </Td>
                      <Td>
                        <span>{Array.isArray(provider.models) ? provider.models.join(', ') : '-'}</span>
                      </Td>
                      <Td>
                        {provider.enabled ? (
                          <Badge bg="#d1fae5" color="#059669">
                            <Check size={14} />
                            Активен
                          </Badge>
                        ) : (
                          <Badge bg="#fee2e2" color="#dc2626">
                            <X size={14} />
                            Выключен
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Button bg="#4f46e5" onClick={() => handleEdit(provider)}>
                            <Edit size={14} />
                            Редактировать
                          </Button>
                          <Button bg="#dc2626" onClick={() => handleDelete(provider.id, provider.name)}>
                            <Trash2 size={14} />
                            Удалить
                          </Button>
                        </div>
                      </Td>
                    </tr>
                  )
                ))}
              </tbody>
            </Table>
          </TableContainer>
          <div style={{ marginTop: '16px', textAlign: 'center', color: '#6b7280' }}>
            Всего: {data.data.total || 0}
          </div>
        </>
      )}
    </Container>
  );
}

export default Providers;
